import { useState, useMemo } from 'react'
import { ChevronLeft, ChevronRight, Search } from 'lucide-react'
import type { Cdi } from '../../data/cdi'
import { AMBITI_STATS } from '../../data/ambitiStats'
import { CdiDialog } from '../StrutturaDetailDialog'

const PAGE_SIZE = 10
const COLOR = '#10b981'

interface Props {
  cdi: Cdi[]
}

export default function CdiDialogList({ cdi }: Props) {
  const [search, setSearch] = useState('')
  const [activeTag, setActiveTag] = useState<string | null>(null)
  const [selectedCdi, setSelectedCdi] = useState<Cdi | null>(null)
  const [page, setPage] = useState(0)

  const tags = useMemo(() => {
    const counts = new Map<string, number>()
    for (const c of cdi) {
      const ambito = AMBITI_STATS.find(a => a.id === c.ambitoId)
      const key = ambito ? `Ambito di ${ambito.nome}` : 'Altro'
      counts.set(key, (counts.get(key) ?? 0) + 1)
    }
    return Array.from(counts.entries())
      .sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0], 'it'))
      .map(([name]) => ({ name }))
  }, [cdi])

  const getTag = (c: Cdi) => {
    const ambito = AMBITI_STATS.find(a => a.id === c.ambitoId)
    return ambito ? `Ambito di ${ambito.nome}` : 'Altro'
  }

  const filtered = useMemo(() => {
    const term = search.toLowerCase()
    return cdi.filter(c => {
      if (activeTag && getTag(c) !== activeTag) return false
      if (term) {
        const text = `${c.nome} ${c.localita}`.toLowerCase()
        if (!text.includes(term)) return false
      }
      return true
    })
  }, [cdi, search, activeTag])

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE))
  const safePage = Math.min(page, totalPages - 1)
  if (safePage !== page) setPage(safePage)

  const paged = filtered.slice(safePage * PAGE_SIZE, (safePage + 1) * PAGE_SIZE)

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 0, height: '100%', minHeight: '50vh' }}>
      {/* Search */}
      <div style={{ flexShrink: 0, position: 'relative', marginBottom: 8 }}>
        <Search size={14} style={{ position: 'absolute', left: 8, top: '50%', transform: 'translateY(-50%)', color: 'var(--text-3)' }} />
        <input
          type="text"
          placeholder="Cerca CDI…"
          value={search}
          onChange={e => { setSearch(e.target.value); setPage(0) }}
          style={{
            width: '100%',
            padding: '7px 8px 7px 28px',
            fontSize: 12,
            border: '1px solid var(--border)',
            borderRadius: 6,
            background: 'var(--surface-2, var(--background))',
            color: 'var(--text-1)',
            outline: 'none',
          }}
        />
      </div>

      {/* Tag filters */}
      <div style={{
        flexShrink: 0,
        display: 'flex',
        flexWrap: 'wrap',
        gap: 4,
        marginBottom: 10,
        maxHeight: 64,
        overflowY: 'auto',
      }}>
        {tags.map(tag => {
          const isActive = activeTag === tag.name
          return (
            <button
              key={tag.name}
              type="button"
              onClick={() => { setActiveTag(isActive ? null : tag.name); setPage(0) }}
              style={{
                padding: '3px 8px',
                fontSize: 10,
                fontWeight: 600,
                borderRadius: 12,
                border: `1px solid ${isActive ? COLOR : 'var(--border)'}`,
                background: isActive ? `${COLOR}20` : 'transparent',
                color: isActive ? COLOR : 'var(--text-2)',
                cursor: 'pointer',
                whiteSpace: 'nowrap',
                lineHeight: 1.4,
              }}
            >
              {tag.name}
            </button>
          )
        })}
      </div>

      {/* List */}
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto', overflowX: 'hidden', display: 'grid', gap: 2 }}>
        {paged.map(c => (
          <div
            key={c.id}
            onClick={() => setSelectedCdi(c)}
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: 8,
              padding: '6px 8px',
              borderRadius: 6,
              cursor: 'pointer',
              borderLeft: `3px solid ${COLOR}`,
              background: 'var(--surface-2, transparent)',
            }}
          >
            <div style={{ minWidth: 0, flex: 1 }}>
              <div style={{ fontSize: 12, fontWeight: 600, color: 'var(--text-1)', wordBreak: 'break-word' }}>
                {c.nome}
              </div>
              <div style={{ fontSize: 10, color: 'var(--text-3)', marginTop: 1 }}>
                {c.localita}
              </div>
            </div>
            <span style={{
              flexShrink: 0,
              padding: '2px 6px',
              fontSize: 9,
              fontWeight: 600,
              borderRadius: 8,
              background: `${COLOR}18`,
              color: COLOR,
              whiteSpace: 'nowrap',
            }}>
              {c.postiAccreditati} posti
            </span>
          </div>
        ))}
        {filtered.length === 0 && (
          <div style={{ fontSize: 12, color: 'var(--text-3)', padding: 16, textAlign: 'center' }}>
            Nessun CDI trovato
          </div>
        )}
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div style={{
          flexShrink: 0,
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          gap: 8,
          paddingTop: 8,
          borderTop: '1px solid var(--border)',
          marginTop: 8,
        }}>
          <button
            type="button"
            disabled={safePage === 0}
            onClick={() => setPage(p => Math.max(0, p - 1))}
            style={{
              background: 'none', border: 'none', cursor: safePage === 0 ? 'default' : 'pointer',
              color: safePage === 0 ? 'var(--text-3)' : 'var(--text-1)', padding: 4,
              opacity: safePage === 0 ? 0.4 : 1,
            }}
          >
            <ChevronLeft size={16} />
          </button>
          <span style={{ fontSize: 11, color: 'var(--text-2)' }}>
            {safePage + 1} / {totalPages}
          </span>
          <button
            type="button"
            disabled={safePage >= totalPages - 1}
            onClick={() => setPage(p => Math.min(totalPages - 1, p + 1))}
            style={{
              background: 'none', border: 'none', cursor: safePage >= totalPages - 1 ? 'default' : 'pointer',
              color: safePage >= totalPages - 1 ? 'var(--text-3)' : 'var(--text-1)', padding: 4,
              opacity: safePage >= totalPages - 1 ? 0.4 : 1,
            }}
          >
            <ChevronRight size={16} />
          </button>
        </div>
      )}

      <CdiDialog cdi={selectedCdi} onClose={() => setSelectedCdi(null)} fromUrl="/cdi" />
    </div>
  )
}
